import * as React from 'react';
import { t } from '@reos/i18n';
import { Check } from 'lucide-react';
import type { IntakeItemDto } from '../../lib/field-intake-api.js';
import type { ChecklistMark, ChecklistState } from '../../lib/field-intake-checklist.js';
import { INTAKE_FIELD_DEFS } from './fields.js';
import { DISPOSITION_CHOICES, fieldDef, fieldLabel, formatValue, parseManualValue } from './values.js';

/**
 * Krok NAB1 — BODY NABORU JAKO RUCNI CHECKLIST.
 *
 * Nalez R.B. 7. 10. 2026: body v „Ještě nevíme" (Dispozice, Plocha, Stav, …)
 * vypadaly jako zaskrtavatka, ale nereagovaly — plnily se jen z rozboru
 * nahravky, a na mobilu seznam nebyl vubec. Tady je jedna komponenta pro obe
 * sirky: na mobilu rozbalovaci „Co ještě zjistit (n)", na desktopu rozbalena
 * zona pracovni plochy (summary schovava `nabor.css`).
 *
 * Kazdy bod je `role="checkbox"` + `aria-checked` na tlacitku, takze funguje
 * mezernik, Enter i odecitac obrazovky; cely radek je cil klepnuti (>= 44 px,
 * `--rh-touch-h`). Druhe klepnuti bod vrati na „nezjištěno".
 *
 * Hodnotu bodu zapisuje `onMark` — ukladani (fronta + relace naboru) resi
 * obrazovka `/nabor`, komponenta nic neposila.
 */

const tr = (key: Parameters<typeof t>[0], values?: Record<string, string | number>): string =>
  t(key, { locale: 'cs', ...(values !== undefined ? { values } : {}) });

/** Poradi bodu je poradi pevneho seznamu poli; `missing` i znacky mohou prijit v jinem. */
export function checklistKeys(missing: readonly string[], marks: ChecklistState): readonly string[] {
  const wanted = new Set([...missing, ...Object.keys(marks)]);
  return INTAKE_FIELD_DEFS.filter((f) => wanted.has(f.factKey)).map((f) => f.factKey);
}

/**
 * Znacky z navrhu rozboru: `ai` = „zjisteno z nahravky", `manual` = makler
 * sam. Rucni znacka ze stavu obrazovky MA PREDNOST (slucuje volajici),
 * protoze rozbor nahravky nesmi prepsat, co makler zapsal.
 */
export function marksFromItems(items: readonly IntakeItemDto[]): ChecklistState {
  const marks: Record<string, ChecklistMark> = {};
  for (const item of items) {
    if (item.kind !== 'fact' || item.factKey === null || item.status === 'rejected') continue;
    marks[item.factKey] = {
      factKey: item.factKey,
      source: item.origin === 'manual' ? 'manual' : 'recording',
      ...(item.value === null ? {} : { value: item.value }),
    };
  }
  return marks;
}

/**
 * Slouceni znacek z rozboru (`fromItems`) a rucnich (`manual`).
 *
 * RUCNI ZNACKA MA PREDNOST — zadani NAB1: „rozbor nahravky nesmi prepsat rucni
 * hodnotu bez potvrzeni". Jedina vyjimka je rucni odskrtnuti BEZ hodnoty proti
 * znacce z nahravky, ktera hodnotu MA: tam by prednost rucniho zaznamu hodnotu
 * jen schovala, prestoze ji nikdo neprepisuje.
 */
export function mergeMarks(fromItems: ChecklistState, manual: ChecklistState): ChecklistState {
  const out: Record<string, ChecklistMark> = { ...fromItems };
  for (const [key, mark] of Object.entries(manual)) {
    const existing = out[key];
    if (existing === undefined || mark.value !== undefined || existing.value === undefined) out[key] = mark;
  }
  return out;
}

function QuickValue({
  factKey,
  onSave,
  onSkip,
}: {
  readonly factKey: string;
  readonly onSave: (value: unknown) => void;
  readonly onSkip: () => void;
}): React.JSX.Element {
  const [raw, setRaw] = React.useState('');
  const [invalid, setInvalid] = React.useState(false);
  const def = fieldDef(factKey);
  const label = fieldLabel(factKey);

  const save = (text: string): void => {
    const value = parseManualValue(factKey, text);
    if (value === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onSave(value);
  };

  return (
    <div className="rh-fi-quick" data-testid={`fi-point-quick-${factKey}`}>
      {def?.valueType === 'boolean' ? (
        <div className="rh-fi-quick__row">
          <button type="button" className="rh-v4-btn rh-v4-btn--chip" onClick={() => onSave(true)} data-testid={`fi-point-yes-${factKey}`}>
            {tr('webAdmin.fieldIntake.value.yes')}
          </button>
          <button type="button" className="rh-v4-btn rh-v4-btn--chip" onClick={() => onSave(false)} data-testid={`fi-point-no-${factKey}`}>
            {tr('webAdmin.fieldIntake.value.no')}
          </button>
        </div>
      ) : (
        <>
          {factKey === 'disposition' ? (
            <div className="rh-fi-quick__row">
              {DISPOSITION_CHOICES.map((choice) => (
                <button
                  key={choice}
                  type="button"
                  className="rh-v4-btn rh-v4-btn--chip"
                  onClick={() => onSave(choice)}
                  data-testid={`fi-point-choice-${choice}`}
                >
                  {choice}
                </button>
              ))}
            </div>
          ) : null}
          <div className="rh-fi-quick__row">
            <input
              className="rh-input rh-fi-input rh-fi-input--grow"
              value={raw}
              inputMode={def?.valueType === 'number' || def?.valueType === 'integer' ? 'decimal' : def?.valueType === 'money' ? 'numeric' : 'text'}
              aria-label={tr('webAdmin.fieldIntake.checklist.valueLabel', { field: label })}
              onChange={(e) => setRaw(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save(raw);
              }}
              data-testid={`fi-point-value-${factKey}`}
            />
            <button type="button" className="rh-v4-btn rh-v4-btn--chip" onClick={() => save(raw)} data-testid={`fi-point-save-${factKey}`}>
              {tr('webAdmin.fieldIntake.checklist.valueSave')}
            </button>
          </div>
        </>
      )}
      <button type="button" className="rh-v4-btn rh-v4-btn--plain rh-fi-quick__skip" onClick={onSkip} data-testid={`fi-point-skip-${factKey}`}>
        {tr('webAdmin.fieldIntake.checklist.valueSkip')}
      </button>
      {invalid ? (
        <p className="rh-fi-note rh-fi-note--warn" role="alert" data-testid={`fi-point-invalid-${factKey}`}>
          {tr('webAdmin.fieldIntake.checklist.valueInvalid')}
        </p>
      ) : null}
    </div>
  );
}

export function ChecklistPanel({
  keys,
  marks,
  onMark,
  onUnmark,
  defaultOpen,
  withHint = true,
}: {
  readonly keys: readonly string[];
  readonly marks: ChecklistState;
  /** `value === undefined` = bod je zjisteny, hodnotu nezapisujeme. */
  readonly onMark: (factKey: string, value: unknown) => void;
  readonly onUnmark: (factKey: string) => void;
  /** Desktop ma zonu rozbalenou, mobil sbalenou (rozhoduje volajici podle sirky). */
  readonly defaultOpen: boolean;
  readonly withHint?: boolean;
}): React.JSX.Element {
  /*
   * Rozbaleni je stav OBRAZOVKY, ale vychozi hodnota prichazi z props (sirka
   * okna). Kdyz se props zmeni (prepnuti sirky), stav se srovna V RENDERU —
   * `useEffect` by prvni vykresleni nechal se starou hodnotou (nalez
   * `reos-web-admin-mount-effect-race`).
   */
  const [open, setOpen] = React.useState(defaultOpen);
  const [lastDefault, setLastDefault] = React.useState(defaultOpen);
  if (lastDefault !== defaultOpen) {
    setLastDefault(defaultOpen);
    setOpen(defaultOpen);
  }
  /** Ktery bod ma otevrene rychle zadani hodnoty. */
  const [editing, setEditing] = React.useState<string | null>(null);

  const todo = keys.filter((key) => marks[key] === undefined).length;

  const toggle = (factKey: string): void => {
    if (marks[factKey] !== undefined) {
      setEditing(null);
      onUnmark(factKey);
      return;
    }
    onMark(factKey, undefined);
    setEditing(fieldDef(factKey) === undefined ? null : factKey);
  };

  return (
    <details className="rh-v4-group rh-fi-checklist" data-testid="fi-checklist" open={open}>
      <summary
        className="rh-v4-row rh-fi-checklist__toggle"
        data-testid="fi-checklist-toggle"
        onClick={(e) => {
          e.preventDefault();
          setOpen((value) => !value);
        }}
      >
        <span className="rh-v4-row__text">
          <span className="rh-v4-row__title">{tr('webAdmin.fieldIntake.checklist.titleCount', { count: todo })}</span>
        </span>
      </summary>
      {withHint ? <p className="rh-fi-checklist__hint">{tr('webAdmin.fieldIntake.checklist.hint')}</p> : null}
      {keys.length === 0 ? (
        <div className="rh-v4-row rh-v4-row--static rh-fi-muted" data-testid="fi-checklist-empty">
          {tr('webAdmin.fieldIntake.checklist.empty')}
        </div>
      ) : null}
      {keys.map((key) => {
        const mark = marks[key];
        const marked = mark !== undefined;
        return (
          <div key={key} className="rh-fi-point">
            <button
              type="button"
              role="checkbox"
              aria-checked={marked}
              className={`rh-v4-row rh-fi-point__row${marked ? ' is-on' : ''}`}
              onClick={() => toggle(key)}
              data-testid={`fi-point-${key}`}
            >
              {/*
                Zaskrtavatko je TATAZ TRIDA jako v rozboru (`.rh-fi-check`) —
                jeden zdroj vzhledu. Dve skoro stejna kolecka by se rozesla.
              */}
              <span className={`rh-fi-check${marked ? ' is-on' : ''}`} aria-hidden="true">
                {marked ? <Check size={18} strokeWidth={3} /> : null}
              </span>
              <span className="rh-v4-row__text">
                <span className="rh-v4-row__title">{fieldLabel(key)}</span>
                {marked ? (
                  <span className="rh-v4-row__sub" data-testid={`fi-point-source-${key}`}>
                    {mark.source === 'manual'
                      ? tr('webAdmin.fieldIntake.checklist.markedManual')
                      : tr('webAdmin.fieldIntake.checklist.markedRecording')}
                  </span>
                ) : null}
              </span>
              {marked && mark.value !== undefined ? (
                <span className="rh-v4-row__value" data-testid={`fi-point-val-${key}`}>
                  {formatValue(key, mark.value)}
                </span>
              ) : null}
            </button>
            {editing === key ? (
              <QuickValue
                factKey={key}
                onSave={(value) => {
                  onMark(key, value);
                  setEditing(null);
                }}
                onSkip={() => setEditing(null)}
              />
            ) : null}
          </div>
        );
      })}
    </details>
  );
}
